import {
  faPrescriptionBottle,
  faSprinkler,
  faPoop,
  faFlask,
  faPills,
  faCow
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '../../../hooks/use-query';

import Title from '../../../components/common/title';
import Accordion, {
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
} from '../../../components/common/accordion';
import ExcelButton from '../../../components/common/table/excel-button/excel-button';
import ClassifierGroup from '../../../enums/classifier-group';
import DateFilter, {
  DateFields,
} from '../../../components/common/table/date-filter/date-filter';
import { useService } from '../../../services/services';
import css from './index.module.css';
import { RootState } from '../../../store/store';
import { AjaxResponse } from 'rxjs/ajax';
import InfoModal from '../../../components/modals/info-modal';

interface Model {
  name: string;
  service: any;
  icon: any;
  sector: any;
}

export enum JournalKeys {
  MINERAL_FERTILIZERS = 'mineral_fertilizers',
  PLANT_PROTECTION_PRODUCTS = 'plant_protection_agents',
  MANURE = 'manure',
  BIOCIDES = 'biocides',
  VETERINARY_MEDICATION = 'veterinary_medication',
  MILK_ACCOUNTING = 'milk_accounting',
}

export default function ReportJournals() {
  const { t } = useTranslation();
  const [models, setModels] = useState([]);
  const { reportJournalService } = useService();
  const { farmId } = useParams<{ farmId: string }>();
  const { sectors } = useSelector((state: RootState) => ({
    sectors: state.global.farm.sectors,
  }));
  const [infoModal, setInfoModal] = useState(null);

  const [params] = useQuery();
  const dateFilterName = 'report-journal';

  const transKeyPrefix = 'pages.warehouse.report-journals.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);

  const reportJournals = useMemo(
    () => ({
      [JournalKeys.MINERAL_FERTILIZERS]: {
        name: tp(JournalKeys.MINERAL_FERTILIZERS),
        icon: faPrescriptionBottle,
        service: reportJournalService.mineralFertilizerXlsx(farmId, {
          ...params,
        }),
        sector: [ClassifierGroup.CROP],
      },
      [JournalKeys.PLANT_PROTECTION_PRODUCTS]: {
        name: tp(JournalKeys.PLANT_PROTECTION_PRODUCTS),
        icon: faSprinkler,
        service: reportJournalService.plantProtectionProductsXlsx(farmId, {
          ...params,
        }),
        sector: [ClassifierGroup.CROP],
      },
      [JournalKeys.MANURE]: {
        name: tp(JournalKeys.MANURE),
        icon: faPoop,
        service: reportJournalService.manureXlsx(farmId, { ...params }),
        sector: [ClassifierGroup.CROP, ClassifierGroup.LIVESTOCK],
      },
      [JournalKeys.BIOCIDES]: {
        name: tp(JournalKeys.BIOCIDES),
        icon: faFlask,
        service: reportJournalService.biocideXlsx(farmId, { ...params }),
        sector: [ClassifierGroup.CROP, ClassifierGroup.LIVESTOCK],
      },
      [JournalKeys.VETERINARY_MEDICATION]: {
        name: tp(JournalKeys.VETERINARY_MEDICATION),
        icon: faPills,
        service: reportJournalService.veterinaryMedicationXlsx(farmId, {
          ...params,
        }),
        sector: [ClassifierGroup.LIVESTOCK],
      },
      [JournalKeys.MILK_ACCOUNTING]: {
        name: tp(JournalKeys.MILK_ACCOUNTING),
        icon: faCow,
        service: reportJournalService.milkAccountingXlsx(farmId, {
          ...params,
        }),
        sector: [ClassifierGroup.LIVESTOCK],
      },
    }),
    [reportJournalService, tp, params, farmId]
  );

  useEffect(() => {
    setModels(
      Object.values(reportJournals)
        .filter(
          (j: Model) =>
            Object.values(sectors).filter((sector) => j.sector.includes(sector))
              .length
        )
        .sort((a, b) => (a.name > b.name ? 1 : -1))
    );
  }, [reportJournals, sectors]);

  return (
    <>
      <Title title={tp('title')} />
      <div className={css['background-container']}>
        <Accordion>
          {models &&
            models.map((item: Model, index: number) => {
              return (
                <AccordionItem
                  header={
                    <AccordionItemHeader>
                      <h4 className={css['accordion-header']}>
                        {item.icon ? (
                          <span className={css['accordion-icon']}>
                            <FontAwesomeIcon icon={item.icon} />
                          </span>
                        ) : (
                          ''
                        )}
                        {item.name}
                      </h4>
                    </AccordionItemHeader>
                  }
                  body={
                    <AccordionItemBody>
                      <div>
                        <span className={css['accordion-body-text']}>
                          {t('period')}:
                        </span>
                        <DateFilter
                          name={dateFilterName}
                          fields={[DateFields.from, DateFields.to]}
                        />
                        <ExcelButton
                          onClick={() => {
                            if (
                              !params[DateFields.from] ||
                              !params[DateFields.to]
                            ) {
                              setInfoModal(
                                <InfoModal
                                  title={tp('missing_filter_values_title')}
                                  content={tp('missing_filter_values')}
                                  onClose={() => setInfoModal(null)}
                                />
                              );
                            } else {
                              item.service.subscribe(
                                (response: AjaxResponse) => {
                                  if (!response) {
                                    setInfoModal(
                                      <InfoModal
                                        title={item.name}
                                        content={tp('no_data_in_period')}
                                        onClose={() => setInfoModal(null)}
                                      />
                                    );
                                    return;
                                  }
                                  return response;
                                }
                              );
                            }
                          }}
                        />
                      </div>
                    </AccordionItemBody>
                  }
                  opened={index === 0}
                  key={`accordion-item-${index}`}
                />
              );
            })}
        </Accordion>
      </div>
      {infoModal}
    </>
  );
}
